import { StoreInfo } from '../../../external/StoresApi';
import { AgencyInfo, BillcstmInfo, MngcstmInfo, StoreSelectModalState } from '.';
import { getCompanyCfcDisplayName } from '../../../Common';

const SORT_FIELDS = ['kanaName', 'longName', 'shortName', 'companyCfc'] as const;

/**
 * 一次代理店リストを返却する
 *
 * @param stores
 * @returns
 */
export function getBillcstmList(stores: StoreInfo[]): BillcstmInfo[] {
  const billcstmList: BillcstmInfo[] = [];

  stores
    .filter((storeInfo, i, self) => self.findIndex((obj) => obj.billcstmCd === storeInfo.billcstmCd) === i)
    .forEach((storeInfo: StoreInfo) => {
      const billcstmcd = storeInfo.billcstmCd || '';
      if (getMngcstmList(stores, billcstmcd).length > 0) {
        billcstmList.push({
          billcstmCd: storeInfo.billcstmCd || '',
          billcstmLongNm: storeInfo.billcstmLongNm || '',
          isActive: false,
        });
      }
    });

  // billcstmCdに対応するStoreInfoでソート
  // 優先順位: agencyCd === billcstmCd > mngcstmCd === billcstmCd > 最初に見つかった店舗
  billcstmList.sort((a, b) => {
    // a の一次代理店に対応する店舗を検索（agencyCd最優先）
    const storeA =
      stores.find((s) => s.agencyCd === a.billcstmCd) ||
      stores.find((s) => s.mngcstmCd === a.billcstmCd) ||
      stores.find((s) => s.billcstmCd === a.billcstmCd);

    // b の一次代理店に対応する店舗を検索（agencyCd最優先）
    const storeB =
      stores.find((s) => s.agencyCd === b.billcstmCd) ||
      stores.find((s) => s.mngcstmCd === b.billcstmCd) ||
      stores.find((s) => s.billcstmCd === b.billcstmCd);

    if (!storeA) return 1;
    if (!storeB) return -1;

    return compareStoreInfo(storeA, storeB);
  });

  return billcstmList;
}
/**
 * StoreInfoを指定フィールド順でソートする
 */
function compareStoreInfo(storeA: StoreInfo, storeB: StoreInfo): number {
  for (const sortField of SORT_FIELDS) {
    const compare = String(storeA[sortField]).localeCompare(String(storeB[sortField]), 'ja');
    if (compare !== 0) return compare;
  }
  return storeA.agencyCd.localeCompare(storeB.agencyCd, 'ja');
}

/**
 * 指定された一次代理店コードに紐づく運営店リストを返却する
 * 一次代理店コードに紐づく運営店が存在しない。かつ野良の取次店が存在する場合、
 * 「その他」の運営店を生成して返却する
 * @param stores
 * @param billcstmCd
 * @returns
 */
export function getMngcstmList(stores: StoreInfo[], billcstmCd: string): MngcstmInfo[] {
  const mngcstmList: MngcstmInfo[] = [];

  const targetBillcstmStores = stores.filter((storeInfo) => storeInfo.billcstmCd === billcstmCd || !billcstmCd);

  targetBillcstmStores
    .filter((storeInfo) => storeInfo.agencyCd === storeInfo.mngcstmCd)
    .forEach((storeInfo: StoreInfo) => {
      // 運営店配下の取次店が存在する場合
      if (getAgencyList(stores, storeInfo.agencyCd).length > 0) {
        mngcstmList.push({
          agencyCd: storeInfo.agencyCd,
          mngcstmCd: storeInfo.mngcstmCd || '',
          longName: storeInfo.longName,
          billcstmCd: storeInfo.billcstmCd || '',
          isActive: false,
        });
      }
    });

  // その他が0件でない場合、追加
  const dummyMngcstmCd = billcstmCd + '.others';
  const agencyOtherList = getAgencyList(targetBillcstmStores, dummyMngcstmCd);
  if (agencyOtherList.length > 0) {
    mngcstmList.push({
      agencyCd: dummyMngcstmCd,
      mngcstmCd: dummyMngcstmCd,
      longName: 'その他',
      billcstmCd: billcstmCd,
      isActive: false,
    });
  }
  return mngcstmList;
}

/**
 * 指定された運営店コード配下の取次店リストを返却する
 * 指定された運営店コードに紐づく取次店がない。かつ存在する運営店に属さない取次店がいる場合は
 * その取次店リストを返却する
 * @param targetStores
 * @param mngcstmCd
 * @returns
 */
export function getAgencyList(targetStores: StoreInfo[], mngcstmCd: string): AgencyInfo[] {
  const agencyList: AgencyInfo[] = [];

  let tempList: StoreInfo[] = [];

  // その他の処理
  // 取次店コードと運営店コードが一致する取次店（＝運営店）を抽出し、その運営店コードの配列を用意する。
  const mngcstmCdList: string[] = targetStores
    .filter((storeInfo) => storeInfo.agencyCd === storeInfo.mngcstmCd)
    .map((storeInfo: StoreInfo) => storeInfo.mngcstmCd || storeInfo.agencyCd);

  // 指定された運営店コードが配列に含まれない場合、「その他」。運営店は「一次代理店.others」
  if (mngcstmCdList.includes(mngcstmCd)) {
    //その他以外
    tempList = targetStores.filter((storeInfo) => storeInfo.mngcstmCd === mngcstmCd && storeInfo.agencyCd !== mngcstmCd);
  } else {
    //その他
    const otherKeys = mngcstmCd.split('.');
    const billcstmCd = otherKeys[0];
    tempList = targetStores
      //一次代理店配下の野良だけを拾う
      .filter((storeInfo) => storeInfo.billcstmCd === billcstmCd)
      .filter((storeInfo) => !mngcstmCdList.includes(storeInfo.mngcstmCd || ''));
  }

  tempList.forEach((storeInfo: StoreInfo) => {
    agencyList.push({
      agencyCd: storeInfo.agencyCd,
      longName: getCompanyCfcDisplayName(String(storeInfo.companyCfc), storeInfo.longName),
      mngcstmCd: mngcstmCd,
    });
  });

  return agencyList;
}

export function isKeywordsMatch(src: string, keywords: string[]): boolean {
  let result = true;
  keywords.forEach((keyword) => {
    if (src.indexOf(keyword) < 0) {
      result = false;
    }
  });
  return result;
}
export function getFilteredAgencyList(stores: StoreInfo[], keyword: string): StoreInfo[] {
  if (!keyword) {
    return stores.filter((storeInfo) => storeInfo.agencyCd !== storeInfo.mngcstmCd);
  }

  // keywordのvalueの中でspace区切りで配列として変換する必要がある。
  const keywords: string[] = keyword.split(' ').filter((v) => v);

  // Step2. 絞り込みのルール
  // keywordが取次店に含まれている場合は残す。他の場合は除外される。
  const tmpList = stores.filter(
    (storeInfo) =>
      storeInfo.agencyCd !== storeInfo.mngcstmCd &&
      isKeywordsMatch(getCompanyCfcDisplayName(String(storeInfo.companyCfc), storeInfo.longName), keywords)
  );

  // Step最後. 絞り込んだ結果
  // return results;
  return tmpList;
}
export function getFilteredMngcstmList(stores: StoreInfo[], keyword: string): StoreInfo[] {
  const keywords: string[] = keyword.split(' ').filter((v) => v);
  return stores.filter((storeInfo) => storeInfo.agencyCd === storeInfo.mngcstmCd && isKeywordsMatch(storeInfo.longName, keywords));
}
export function getFilteredBillcstmList(stores: StoreInfo[], keyword: string): BillcstmInfo[] {
  const keywords: string[] = keyword.split(' ').filter((v) => v);
  return getBillcstmList(stores).filter((storeInfo) => isKeywordsMatch(storeInfo.billcstmLongNm, keywords));
}

export function setMngcstmList(state: StoreSelectModalState, stores: StoreInfo[], billcstmCd: string) {
  const targetBillcstmStores = stores.filter((storeInfo) => !billcstmCd || storeInfo.billcstmCd === billcstmCd);
  const mngcstmList = getMngcstmList(stores, billcstmCd);
  const mngcstmListActiveItems = mngcstmList.filter((obj) => obj.isActive);
  if (mngcstmList.length || mngcstmListActiveItems.length === 0) {
    mngcstmList[0].isActive = true;
  }
  state.displayedOnMngcstmList = mngcstmList;

  const activeMngcstmCd = mngcstmList.filter((obj) => obj.isActive).length ? mngcstmList.filter((obj) => obj.isActive)[0].mngcstmCd : '';
  setAgencyList(state, targetBillcstmStores, activeMngcstmCd);
}

export function setAgencyList(newState: StoreSelectModalState, targetBillcstmStores: StoreInfo[], activeMngcstmCd: string) {
  const agencyList = getAgencyList(targetBillcstmStores, activeMngcstmCd);
  newState.displayedOnAgencyList = agencyList;
}

export function setStatusMngcstmCheckbox(
  newState: StoreSelectModalState,
  mngcstmList: MngcstmInfo[],
  checkTargetStore: StoreInfo,
  isMngcstmDisplay: boolean,
  stores: StoreInfo[]
) {
  if (checkTargetStore.billcstmCd) {
    const targetMngStore = getMngcstmList(stores, checkTargetStore.billcstmCd).find(
      (storeInfo) => storeInfo.mngcstmCd === checkTargetStore.mngcstmCd
    );

    if (targetMngStore) {
      newState.isMngcstmChecked[targetMngStore.mngcstmCd] = true;
    } else {
      newState.isMngcstmChecked[checkTargetStore.billcstmCd + '.others'] = true;
    }
  } else {
    const targetMngStore = mngcstmList.find((storeInfo) => storeInfo.mngcstmCd === checkTargetStore.mngcstmCd);
    if (isMngcstmDisplay && targetMngStore && 'mngcstmCd' in targetMngStore) {
      newState.isMngcstmChecked[targetMngStore.mngcstmCd] = true;
    }
  }
}

export const convertKeyMngcstmCd = (mngcstmCd: string, billcstmCdParam: string | undefined) => {
  return billcstmCdParam && mngcstmCd === '-' ? `${billcstmCdParam}.others` : mngcstmCd;
};
